import React, { useEffect, useState } from 'react';
import { apiSpCallWithToken } from '@/libs/utils/apiSpCallWithToken.js';

const SpDataViewer = () => {
    // Selection state parameters
    const [currentView, setCurrentView] = useState('well'); 
    const [recordLimit, setRecordLimit] = useState(10);
    
    // Core data lifecycle states
    const [apiRecords, setApiRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch data whenever view or record limit targets shift
    useEffect(() => {
        setLoading(true);
        setError(null);
        
        const proxyClient = apiSpCallWithToken();

        proxyClient.get('/spproxy', {
            params: {
                view: currentView,
                top: recordLimit
            }
        })
        .then(response => {
            const data = response.data;
            // Cleanly peel back data normalization arrays depending on how S&P returns the node tree
            const results = data?.elements || data?.value || (Array.isArray(data) ? data : []);
            setApiRecords(results);
            setLoading(false);
        })
        .catch(err => {
            console.error(`Error loading data view [${currentView}]:`, err);
            setError(err.response?.data?.error || err.message || `Failed to fetch data for ${currentView}.`);
            setLoading(false);
        });
    }, [currentView, recordLimit]);

    // Helper utility to turn API snake_case keys into readable column headers
    const formatHeaderLabel = (key) => {
        return key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    // Dynamically safely extract table columns based on whatever keys show up in the first object
    const getDynamicHeaders = () => {
        if (!apiRecords || apiRecords.length === 0) return [];
        // Extract up to 10 columns so the screen doesn't break if S&P spits back 60+ payload properties
        return Object.keys(apiRecords[0]).slice(0, 12);
    };

    const targetColumns = getDynamicHeaders();

    return (
        <div style={{ padding: "20px", fontFamily: "sans-serif" }}>
            <h2 style={{ marginBottom: "5px" }}>S&P Global Energy Data Hub</h2>
            <p style={{ color: "#666", fontSize: "14px", marginTop: "0", marginBottom: "20px" }}>
                Querying authenticated upstream data directly via secure .NET application layer proxy.
            </p>

            {/* Selection & Controls Bar */}
            <div style={{ 
                display: "flex", 
                gap: "15px", 
                alignItems: "center", 
                padding: "15px", 
                background: "#f8f9fa", 
                borderRadius: "6px",
                marginBottom: "20px",
                border: "1px solid #e9ecef"
            }}>
                <div>
                    <label htmlFor="viewSelector" style={{ fontWeight: "bold", marginRight: "8px", fontSize: "14px" }}>Dataset View:</label>
                    <select 
                        id="viewSelector"
                        value={currentView} 
                        onChange={(e) => setCurrentView(e.target.value)}
                        style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #ccc" }}
                    >
                        <option value="well">Well Headers</option>
                        <option value="pressure_profile">Pressure Profile</option>
                        <option value="dst_summary">DST Summary</option>
                        <option value="tops">Formation Tops</option>
                        <option value="grad">Pressure Gradients</option>
                        <option value="temperature">Temperature</option>
                        <option value="intform">Interpreted Formation Pressures</option>
                    </select>
                </div>

                <div>
                    <label htmlFor="limitSelector" style={{ fontWeight: "bold", marginRight: "8px", fontSize: "14px" }}>Max Records:</label>
                    <select 
                        id="limitSelector"
                        value={recordLimit} 
                        onChange={(e) => setRecordLimit(Number(e.target.value))}
                        style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #ccc" }}
                    >
                        <option value={10}>10 Rows</option>
                        <option value={25}>25 Rows</option>
                        <option value={50}>50 Rows</option>
                    </select>
                </div>
            </div>

            {/* Dynamic Rendering Core Screen Blocks */}
            {loading && (
                <div style={{ padding: "30px", textAlign: "center", background: "#fdfdfd", border: "1px dashed #ccc" }}>
                    <p style={{ margin: 0, fontWeight: "500", color: "#555" }}>
                        Streaming dynamic chunks for <strong>{currentView}</strong>...
                    </p>
                </div>
            )}

            {error && (
                <div style={{ padding: "15px", backgroundColor: "#fff5f5", color: "#c53030", borderRadius: "4px", border: "1px solid #fed7d7" }}>
                    <strong>Upstream Data Error:</strong> {error}
                </div>
            )}

            {!loading && !error && (
                <>
                    {apiRecords.length > 0 ? (
                        <div style={{ overflowX: "auto", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", borderRadius: "4px" }}>
                            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px', textAlign: 'left' }}>
                                <thead style={{ background: '#e2e8f0', color: '#1a202c' }}>
                                    <tr>
                                        {targetColumns.map(col => (
                                            <th key={col} style={{ padding: "10px 12px", borderBottom: "2px solid #cbd5e0", fontWeight: "bold" }}>
                                                {formatHeaderLabel(col)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {apiRecords.map((record, rowIndex) => (
                                        <tr 
                                            key={record.uwi || record.id || rowIndex} 
                                            style={{ backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f7fafc', borderBottom: "1px solid #e2e8f0" }}
                                        >
                                            {targetColumns.map(col => {
                                                const val = record[col];
                                                let displayVal = 'N/A';

                                                if (val !== null && val !== undefined) {
                                                    if (typeof val === 'object') {
                                                        displayVal = JSON.stringify(val);
                                                    } else if (typeof val === 'number') {
                                                        displayVal = Number.isInteger(val) ? val.toString() : val.toFixed(4);
                                                    } else if (typeof val === 'string' && val.includes('T00:00:00')) {
                                                        displayVal = val.split('T')[0]; // Clean date timestamps
                                                    } else {
                                                        displayVal = val.toString();
                                                    }
                                                }

                                                return (
                                                    <td key={col} style={{ padding: "10px 12px", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden", maxWidth: "200px" }}>
                                                        {displayVal}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={{ padding: "20px", textAlign: "center", color: "#718096", backgroundColor: "#edf2f7", borderRadius: "4px" }}>
                            No raw data structures parsed for view "{currentView}". This endpoint may require specific region entitlements.
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SpDataViewer;